import './HSwipeList.scss';

import * as tsx from 'vue-tsx-support';
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { HIcon } from '~/components';

// eslint-disable-next-line no-undef
type JSXElement = VueTsxSupport.JSX.Element;

/**
 * スワイプ機能を提供するハンドラークラス
 */
class SwipeHandler {
  private vm: Vue;
  private containerSelector: string;
  private leftArrowSelector: string;
  private rightArrowSelector: string;
  private firstChildSelector: string;
  private lastChildSelector: string;
  private scrollAmount: number;
  private observers: IntersectionObserver[] = [];

  constructor(
    vm: Vue,
    selectors: {
      container: string;
      leftArrow: string;
      rightArrow: string;
      firstChild: string;
      lastChild: string;
    },
    scrollAmount: number = 400,
  ) {
    this.vm = vm;
    this.containerSelector = selectors.container;
    this.leftArrowSelector = selectors.leftArrow;
    this.rightArrowSelector = selectors.rightArrow;
    this.firstChildSelector = selectors.firstChild;
    this.lastChildSelector = selectors.lastChild;
    this.scrollAmount = scrollAmount;
  }

  /** IntersectionObserverをクリーンアップする処理 */
  cleanupObservers() {
    this.observers.forEach((observer) => observer.disconnect());
    this.observers = [];

    const leftArrow = this.vm.$el.querySelector(this.leftArrowSelector);
    const rightArrow = this.vm.$el.querySelector(this.rightArrowSelector);

    if (leftArrow instanceof HTMLElement) {
      leftArrow.classList.remove('is-visible');
      leftArrow.classList.add('is-hidden');
    }
    if (rightArrow instanceof HTMLElement) {
      rightArrow.classList.remove('is-visible');
      rightArrow.classList.add('is-hidden');
    }
  }

  /** IntersectionObserverを設定 */
  setupIntersectionObserver() {
    const list = this.vm.$el.querySelector(this.containerSelector);
    const firstItem = this.vm.$el.querySelector(this.firstChildSelector);
    const lastItem = this.vm.$el.querySelector(this.lastChildSelector);
    const leftArrow = this.vm.$el.querySelector(this.leftArrowSelector);
    const rightArrow = this.vm.$el.querySelector(this.rightArrowSelector);

    // 左矢印
    if (list && firstItem && leftArrow instanceof HTMLElement) {
      const firstItemObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              leftArrow.classList.add('is-visible');
              leftArrow.classList.remove('is-hidden');
            } else {
              leftArrow.classList.remove('is-visible');
              leftArrow.classList.add('is-hidden');
            }
          });
        },
        { root: list, threshold: 0.5, rootMargin: '0px' },
      );
      firstItemObserver.observe(firstItem);
      this.observers.push(firstItemObserver);
      leftArrow.classList.add('is-hidden');
    }

    // 右矢印
    if (list && lastItem && rightArrow instanceof HTMLElement) {
      const lastItemObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              rightArrow.classList.add('is-visible');
              rightArrow.classList.remove('is-hidden');
            } else {
              rightArrow.classList.remove('is-visible');
              rightArrow.classList.add('is-hidden');
            }
          });
        },
        { root: list, threshold: 0.5, rootMargin: '0px' },
      );
      lastItemObserver.observe(lastItem);
      this.observers.push(lastItemObserver);

      // 右矢印の初期表示設定
      if (list instanceof HTMLElement) {
        // スクロールが可能かどうかを確認（コンテンツの幅がコンテナより大きいか）
        const canScroll = list.scrollWidth > list.clientWidth;
        if (canScroll) {
          rightArrow.classList.add('is-visible');
          rightArrow.classList.remove('is-hidden');
        } else {
          rightArrow.classList.remove('is-visible');
          rightArrow.classList.add('is-hidden');
        }
      }
    }
  }

  /** 動的なスクロール量を計算 */
  private getScrollAmount(): number {
    const list = this.vm.$el.querySelector(this.containerSelector);
    const firstChild = this.vm.$el.querySelector(this.firstChildSelector);

    if (list instanceof HTMLElement && firstChild instanceof HTMLElement) {
      // 子要素の幅 + gap を取得
      const childWidth = firstChild.offsetWidth;
      const computedStyle = window.getComputedStyle(list);
      const gap = parseFloat(computedStyle.gap) || 0;

      return childWidth + gap;
    }

    // フォールバック: 設定されたscrollAmountを使用
    return this.scrollAmount;
  }

  /** 左スクロール */
  handleScrollLeft = (event: Event) => {
    event.preventDefault();
    const list = this.vm.$el.querySelector(this.containerSelector);
    if (list instanceof HTMLElement) {
      const scrollAmount = this.getScrollAmount();
      list.scrollTo({
        left: list.scrollLeft - scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  /** 右スクロール */
  handleScrollRight = (event: Event) => {
    event.preventDefault();
    const list = this.vm.$el.querySelector(this.containerSelector);
    if (list instanceof HTMLElement) {
      const scrollAmount = this.getScrollAmount();
      list.scrollTo({
        left: list.scrollLeft + scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  onBeforeDestroy() {
    this.cleanupObservers();
  }
}

export interface HSwipeListProps {
  /** 表示するアイテムの配列 */
  items: any[];
  /** 各アイテムをレンダリングする関数 */
  renderItem: (props: { item: any; index: number }) => JSXElement;
  /** スクロール量（デフォルト: 400px） */
  scrollAmount?: number;
}

export interface HSwipeListEmits {}

export interface HSwipeListScopedSlots {}

@Component<HSwipeListRef>({
  name: 'HSwipeList',
  created() {
    // スワイプハンドラーを初期化
    this.swipeHandler = new SwipeHandler(
      this,
      {
        container: '.h-swipe-list__container',
        leftArrow: '.h-swipe-list__arrow--left',
        rightArrow: '.h-swipe-list__arrow--right',
        firstChild: '.h-swipe-list__item:first-child',
        lastChild: '.h-swipe-list__item:last-child',
      },
      this.scrollAmount || 400,
    );
  },
  mounted() {
    // CSSでレスポンシブ対応、IntersectionObserver設定は必要時のみ
    this.$nextTick(() => {
      this.swipeHandler.setupIntersectionObserver();
    });
  },
  beforeDestroy() {
    this.swipeHandler.onBeforeDestroy();
  },
  render() {
    const { items, renderItem } = this;

    return (
      <div staticClass="h-swipe-list">
        <button
          staticClass="h-swipe-list__arrow h-swipe-list__arrow--left"
          onClick={this.swipeHandler.handleScrollLeft}>
          <HIcon
            staticClass="h-swipe-list__arrow-icon"
            name="keyboard-arrow-left"
          />
        </button>

        <div staticClass="h-swipe-list__container">
          {items.map((item, index) => (
            <div key={item.key || index} staticClass="h-swipe-list__item">
              {renderItem({ item, index })}
            </div>
          ))}
        </div>

        <button
          staticClass="h-swipe-list__arrow h-swipe-list__arrow--right"
          onClick={this.swipeHandler.handleScrollRight}>
          <HIcon
            staticClass="h-swipe-list__arrow-icon"
            name="keyboard-arrow-right"
          />
        </button>
      </div>
    );
  },
})
export class HSwipeListRef extends Vue implements HSwipeListProps {
  @Prop({ type: Array, required: true })
  readonly items!: any[];

  @Prop({ type: Function, required: true })
  readonly renderItem!: (props: { item: any; index: number }) => JSXElement;

  @Prop({ type: Number, default: 400 })
  readonly scrollAmount!: number;

  /** スワイプハンドラー */
  private swipeHandler!: SwipeHandler;
}

export const HSwipeList = tsx
  .ofType<HSwipeListProps, HSwipeListEmits, HSwipeListScopedSlots>()
  .convert(HSwipeListRef);
